import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Globe, User, LogOut, Calendar, TrendingUp, Users, LayoutDashboard, UserCircle, ClipboardList } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { useSiteSettings } from '../hooks/useSiteSettings';
import NotificationBell from './NotificationBell';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const currentPathRef = useRef('');
  const { user, isAuthenticated, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const { settings, getImageUrl } = useSiteSettings();
  const location = useLocation();
  const navigate = useNavigate();
  currentPathRef.current = location.pathname;

  const brandName = settings?.brand_name || "Lou's Way";
  const brandLogo = getImageUrl(settings?.brand_logo);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // iOS Safari: o menu é um painel com scroll próprio. Bloquear o fundo
  // evita que os botões inferiores fiquem inacessíveis durante o movimento da barra do browser.
  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const initialPath = currentPathRef.current;
    const scrollY = window.scrollY;
    const previousBody = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    const media = window.matchMedia('(min-width: 1024px)');
    const onDesktop = () => { if (media.matches) setIsMobileMenuOpen(false); };
    document.addEventListener('keydown', onKeyDown);
    if (media.addEventListener) media.addEventListener('change', onDesktop);
    else media.addListener(onDesktop);
    onDesktop();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (media.removeEventListener) media.removeEventListener('change', onDesktop);
      else media.removeListener(onDesktop);
      document.body.style.position = previousBody.position;
      document.body.style.top = previousBody.top;
      document.body.style.left = previousBody.left;
      document.body.style.right = previousBody.right;
      document.body.style.width = previousBody.width;
      document.body.style.overflow = previousBody.overflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      // Ao navegar para outra página, não devolver o scroll da página anterior.
      if (currentPathRef.current === initialPath) window.scrollTo(0, scrollY);
    };
  }, [isMobileMenuOpen]);

  // Fechar o menu ao mudar de página, inclusive por navegação externa ao próprio menu.
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/about', label: t('nav.about') },
    { href: '/services', label: t('nav.services') },
    { href: '/culture', label: t('nav.culture') },
    { href: '/mission', label: t('nav.mission') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        isMobileMenuOpen ? 'flex flex-col overflow-hidden bg-white shadow-sm' :
        isScrolled ? 'nav-glass shadow-sm bg-white' : 'bg-transparent'
      } ${isMobileMenuOpen ? 'h-screen' : ''}`}
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        ...(isMobileMenuOpen ? { height: '100dvh', maxHeight: '100dvh' } : {}),
      }}
      data-testid="navbar"
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-20 shrink-0">
        <nav className="flex items-center justify-between h-20 min-w-0">
          {/* Logo - Ajustada para não ficar minúscula */}
          <Link
            to="/"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center min-w-0 transition-transform hover:scale-105 active:scale-95"
            data-testid="navbar-logo"
          >
            {brandLogo ? (
              <img 
                src={brandLogo} 
                alt={brandName} 
                className="h-28 md:h-36 w-auto object-contain -my-10" 
                /* O -my-10 compensa o excesso de margem transparente da imagem */
              />
            ) : (
              <span className="font-heading text-2xl font-semibold text-[#1A1815]">
                {brandName}
              </span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm uppercase tracking-widest transition-colors ${
                  isActive(link.href)
                    ? 'text-[#BFA05F]'
                    : 'text-[#2C2924] hover:text-[#BFA05F]'
                }`}
                data-testid={`nav-link-${link.href.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="text-[#2C2924] hover:text-[#BFA05F] hover:bg-transparent"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4 mr-2" />
              {language.toUpperCase()}
            </Button>

            {isAuthenticated ? (
              <>
                <Button
                  onClick={() => navigate('/booking')}
                  className="bg-[#1A1815] text-white hover:bg-[#2C2924] rounded-full px-6"
                  data-testid="book-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {t('nav.book')}
                </Button>

                <NotificationBell />

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex items-center gap-2 hover:bg-transparent"
                      data-testid="user-menu-trigger"
                    >
                      {user?.picture ? (
                        <img
                          src={user.picture}
                          alt={user.name}
                          className="w-8 h-8 rounded-full object-cover border-2 border-[#BFA05F]"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#BFA05F] flex items-center justify-center text-white text-sm">
                          {user?.name?.charAt(0) || 'U'}
                        </div>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem
                      onClick={() => navigate('/dashboard')}
                      className="cursor-pointer"
                      data-testid="menu-dashboard"
                    >
                      <User className="w-4 h-4 mr-2" />
                      {t('nav.dashboard')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/profile')}
                      className="cursor-pointer"
                      data-testid="menu-profile"
                    >
                      <UserCircle className="w-4 h-4 mr-2" />
                      {language === 'pt' ? 'Meu Perfil' : 'My Profile'}
                    </DropdownMenuItem>
                    {(user?.role === 'admin' || user?.role === 'staff') && (
                      <>
                        <DropdownMenuItem
                          onClick={() => navigate('/schedule')}
                          className="cursor-pointer"
                          data-testid="menu-schedule"
                        >
                          <Calendar className="w-4 h-4 mr-2" />
                          {language === 'pt' ? 'Agenda' : 'Schedule'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate('/gestao-lista-espera')}
                          className="cursor-pointer"
                          data-testid="menu-waitlist-management"
                        >
                          <ClipboardList className="w-4 h-4 mr-2" />
                          {language === 'pt' ? 'Gestão da Lista de Espera' : 'Waitlist Management'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate('/staff-dashboard')}
                          className="cursor-pointer"
                          data-testid="menu-staff-dashboard"
                        >
                          <TrendingUp className="w-4 h-4 mr-2" />
                          {language === 'pt' ? 'Faturação e Horários' : 'Revenue & Schedule'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate('/user-management')}
                              className="cursor-pointer"
                              data-testid="menu-user-management"
                            >
                              <Users className="w-4 h-4 mr-2" />
                          {language === 'pt' ? 'Gestão de Utilizadores' : 'User Management'}
                        </DropdownMenuItem>
                        {user?.role === 'admin' && (
                          <DropdownMenuItem
                              onClick={() => navigate('/admin')}
                              className="cursor-pointer"
                              data-testid="menu-admin"
                            >
                              <LayoutDashboard className="w-4 h-4 mr-2" />
                              {t('admin.title')}
                            </DropdownMenuItem>
                        )}
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={logout}
                      className="cursor-pointer text-red-600"
                      data-testid="menu-logout"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                className="bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full px-6"
                data-testid="login-btn"
              >
                {t('nav.login')}
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center shrink-0 gap-1">
            {isAuthenticated && <NotificationBell />}
            <button
              ref={menuButtonRef}
              type="button"
              aria-label={isMobileMenuOpen ? (language === 'pt' ? 'Fechar menu' : 'Close menu') : (language === 'pt' ? 'Abrir menu' : 'Open menu')}
              aria-expanded={isMobileMenuOpen}
              aria-controls="lousway-mobile-menu"
              className="relative z-[60] inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg p-2 text-[#1A1815] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#BFA05F]"
              onClick={() => setIsMobileMenuOpen(open => !open)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-[#1A1815]" />
              ) : (
                <Menu className="w-6 h-6 text-[#1A1815]" />
              )}
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          id="lousway-mobile-menu"
          className="lg:hidden min-h-0 flex-1 w-full overflow-x-hidden overflow-y-auto overscroll-contain border-t border-[#E6E2DA] bg-white"
          role="navigation"
          aria-label={language === 'pt' ? 'Menu para telemóvel' : 'Mobile navigation'}
          style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y', scrollbarWidth: 'thin' }}
        >
          <div
            className="px-4 sm:px-6 pt-5 space-y-3"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 44px)' }}
          >
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex min-h-11 items-center text-sm uppercase tracking-widest py-2 rounded-lg ${
                  isActive(link.href)
                    ? 'text-[#BFA05F]'
                    : 'text-[#2C2924]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-[#E6E2DA] space-y-3">
              <Button
                variant="outline"
                onClick={toggleLanguage}
                className="w-full min-h-11 h-auto py-3 px-4 justify-center whitespace-normal"
              >
                <Globe className="w-4 h-4 mr-2 shrink-0" />
                {language === 'pt' ? 'English' : 'Português'}
              </Button>
              {isAuthenticated ? (
                <>
                  <Button
                    onClick={() => {
                      navigate('/booking');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full min-h-11 h-auto py-3 px-4 justify-start text-left whitespace-normal break-words bg-[#BFA05F] text-white"
                  >
                    <Calendar className="w-4 h-4 mr-2 shrink-0" />
                    {t('nav.book')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigate('/dashboard');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full min-h-11 h-auto py-3 px-4 justify-start text-left whitespace-normal break-words border-[#E6E2DA]"
                  >
                    <User className="w-4 h-4 mr-2 shrink-0" />
                    {t('nav.dashboard')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigate('/profile');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full min-h-11 h-auto py-3 px-4 justify-start text-left whitespace-normal break-words border-[#E6E2DA]"
                  >
                    <UserCircle className="w-4 h-4 mr-2 shrink-0" />
                    {language === 'pt' ? 'Meu Perfil' : 'My Profile'}
                  </Button>
                  
                  {/* Rotas de Staff e Admin no Mobile */}
                  {(user?.role === 'admin' || user?.role === 'staff') && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigate('/schedule');
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full min-h-11 h-auto py-3 px-4 justify-start text-left whitespace-normal break-words border-[#E6E2DA]"
                      >
                        <Calendar className="w-4 h-4 mr-2 shrink-0" />
                        {language === 'pt' ? 'Agenda' : 'Schedule'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigate('/gestao-lista-espera');
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full min-h-11 h-auto py-3 px-4 justify-start text-left whitespace-normal break-words border-[#E6E2DA]"
                        data-testid="mobile-menu-waitlist-management"
                      >
                        <ClipboardList className="w-4 h-4 mr-2 shrink-0" />
                        {language === 'pt' ? 'Gestão da Lista de Espera' : 'Waitlist Management'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigate('/staff-dashboard');
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full min-h-11 h-auto py-3 px-4 justify-start text-left whitespace-normal break-words border-[#E6E2DA]"
                      >
                        <TrendingUp className="w-4 h-4 mr-2 shrink-0" />
                        {language === 'pt' ? 'Faturação e Horários' : 'Revenue & Schedule'}
                      </Button>
                    </>
                  )}
                  {(user?.role === 'admin' || user?.role === 'staff') && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigate('/user-management');
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full min-h-11 h-auto py-3 px-4 justify-start text-left whitespace-normal break-words border-[#E6E2DA]"
                      >
                        <Users className="w-4 h-4 mr-2 shrink-0" />
                        {language === 'pt' ? 'Gestão Utilizadores' : 'User Management'}
                      </Button>
                  )}
                  {user?.role === 'admin' && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigate('/admin');
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full min-h-11 h-auto py-3 px-4 justify-start text-left whitespace-normal break-words border-[#E6E2DA]"
                      >
                        <LayoutDashboard className="w-4 h-4 mr-2 shrink-0" />
                        {t('admin.title')}
                      </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => {
                      logout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full min-h-11 h-auto py-3 px-4 justify-start text-left whitespace-normal break-words text-red-600 border-red-200"
                  >
                    <LogOut className="w-4 h-4 mr-2 shrink-0" />
                    {t('nav.logout')}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => {
                    navigate('/login');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full min-h-11 h-auto py-3 px-4 justify-start text-left whitespace-normal break-words bg-[#BFA05F] text-white"
                >
                  {t('nav.login')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;